import { useCallback, useEffect, useId } from 'react';

import type { ApiRecipe } from '../../../../../../models/recipe/recipeModel';
import { RecipeTeaserSkeleton } from '../../../../../common/components/BaseRecipeTeaser';
import BuyableFoodboxTeaser from '../../../../../common/components/BuyableFoodboxTeaser';
import BuyableRecipeTeaser from '../../../../../common/components/BuyableRecipeTeaser';
import { useListBlockLazyLoading } from '../../../../../common/components/ListBlock';
import ListBlock from '../../../../../common/components/ListBlock/ListBlock';
import { BlockHeader } from '../../../../../common/components/ListBlock/ListBlockHeader';
import RecipeTypeComponentPicker from '../../../../../common/components/RecipeTypeComponentPicker';
import { useAsyncDispatcher } from '../../../../../common/hooks/useAsyncDispatcher';
import useBuyableRecipesBreakpoints from '../../../../../common/hooks/useBuyableRecipesBreakpoints';
import { useAppSelector } from '../../../../../common/hooks/useThunkDispatch';
import { storeSelectors } from '../../../../../common/selectors/storeSelectors';
import { ga4RecipesTracking } from '../../../../../common/tracking/ga4/recipes';
import recipeFlow from '../../../../../recipe/flow/recipeFlow';

interface RecipeRecommendationsByProductProps {
    productIdentifier: string;
}

const RecipeRecommendationsByProduct = (props: RecipeRecommendationsByProductProps) => {
    const { execute, result, isLoading } = useAsyncDispatcher(recipeFlow.getRecipesByProduct, {
        initialLoadingState: true,
    });

    const currentProductionUnit = useAppSelector(storeSelectors.currentProductionUnit);

    const getRecipes = useCallback(() => {
        if (props.productIdentifier) {
            execute([props.productIdentifier], currentProductionUnit, 20);
        }
    }, [execute, currentProductionUnit, props.productIdentifier]);

    const { swiperOptions, mainSwiperClasses, cellsInRow } = useBuyableRecipesBreakpoints();

    const [inViewRef, inView] = useListBlockLazyLoading();

    useEffect(() => {
        if (inView) {
            getRecipes();
        }
    }, [inView, getRecipes]);

    const headerId = useId();

    if (!isLoading && !result?.length) {
        return null;
    }

    return (
        <ListBlock
            aria-labelledby={headerId}
            skipLinkOptions={{
                'aria-describedby': headerId,
            }}
            as="aside"
            ref={inViewRef}
            headerSlot={!isLoading && <BlockHeader id={headerId}>Passande recept</BlockHeader>}
            isLoading={isLoading}
            items={result}
            useSwiper
            swiperOptions={swiperOptions}
            swiperMainClassName={mainSwiperClasses}
            cellsInRow={cellsInRow}
            gridClasses="Grid--gutterAxsm"
            analyticsListName="Passande recept"
            horizontalHidden={false}
            itemUniqueKey={(item) => item.externalId.toString()}
            renderItem={(recipe) => <RecipeItem recipe={recipe} />}
            renderSkeleton={() => <RecipeTeaserSkeleton />}
        />
    );
};

const RecipeItem: React.FC<React.PropsWithChildren<{ recipe: ApiRecipe }>> = ({ recipe }) => {
    const handleBuyClick = (recipeExternalId: number, recipeName: string) => () => {
        ga4RecipesTracking.pdpRecipeRecommendations.buyButtonClick(recipeExternalId, recipeName);
    };

    const handleLinkClick = (recipeExternalId: number, recipeName: string) => () => {
        ga4RecipesTracking.pdpRecipeRecommendations.recipeTeaserClick(recipeExternalId, recipeName);
    };

    return (
        <RecipeTypeComponentPicker
            isFoodbox={recipe.isFoodBox}
            recipeSlot={
                <BuyableRecipeTeaser
                    id={recipe.externalId.toString()}
                    name={recipe.name}
                    url={recipe.relativeUrl}
                    rating={recipe.avgRating}
                    co2ImpactKg={recipe.climateImpactKg}
                    imageUrl={recipe.imageUrl}
                    votes={recipe.numberOfVotes}
                    defaultPortions={recipe.yieldValue}
                    analyticsListName="PLP - Recipemodal"
                    onBuyClick={handleBuyClick(recipe.externalId, recipe.name)}
                    onLinkClick={handleLinkClick(recipe.externalId, recipe.name)}
                />
            }
            foodboxSlot={
                <BuyableFoodboxTeaser
                    id={recipe.externalId.toString()}
                    name={recipe.name}
                    url={recipe.relativeUrl}
                    imageUrl={recipe.imageUrl}
                    defaultPortions={recipe.yieldValue}
                    analyticsListName="PLP - Recipemodal"
                    onBuyClick={handleBuyClick(recipe.externalId, recipe.name)}
                    onLinkClick={handleLinkClick(recipe.externalId, recipe.name)}
                />
            }
        />
    );
};

export default RecipeRecommendationsByProduct;
